// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import translate from "@insite/client-framework/Translate";
import { WarehouseModel } from "@insite/client-framework/Types/ApiModels";
import AddressInfoDisplay, {
    addressInfoDisplayStyles,
    AddressInfoDisplayStyles,
} from "@insite/content-library/Components/AddressInfoDisplay";
import FindLocationModal, { FindLocationModalStyles } from "@insite/content-library/Components/FindLocationModal";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Edit from "@insite/mobius/Icons/Edit";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import sharedAddressesStyles from "@keller/Overrides/Widgets/CheckoutShipping/SharedAddresses.style";
import { iconLinkCommonStyles, mergeWithCss } from "@keller/XDL/Utilities";
import React, { FC } from "react";
import { css } from "styled-components";

export interface PickUpAddressStyles {
    container?: GridContainerProps;
    headingGridItem?: GridItemProps;
    headingText?: TypographyPresentationProps;
    findLocationLink?: LinkPresentationProps;
    selectedLocationGridItem?: GridItemProps;
    selectedLocationName?: TypographyProps;
    selectedLocationAddressDisplay?: AddressInfoDisplayStyles;
    findLocationModal?: FindLocationModalStyles;
}

export const pickUpAddressStyles: PickUpAddressStyles = {
    container: {
        ...sharedAddressesStyles.container,
    },
    headingGridItem: {
        ...mergeWithCss(sharedAddressesStyles.headingGridItem, {
            css: css`
                align-items: center;
                gap: 15px;
            `,
        }),
    },
    headingText: {
        ...sharedAddressesStyles.headingText,
    },
    selectedLocationName: {
        ...addressInfoDisplayStyles.companyNameText,
    },
    findLocationLink: {
        ...iconLinkCommonStyles(Edit),
    },
    selectedLocationGridItem: {
        ...sharedAddressesStyles.addressDisplayGridItem,
    },
};

const PickUpAddress: FC<{
    address?: WarehouseModel;
    onChange?: (address: WarehouseModel) => void;
    extendedStyles?: PickUpAddressStyles;
}> = ({ address, onChange, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(pickUpAddressStyles, extendedStyles));
    const [isFindLocationOpen, setIsFindLocationOpen] = React.useState(false);
    const handleOpenFindLocation = () => {
        setIsFindLocationOpen(true);
    };
    const handleFindLocationModalClose = () => setIsFindLocationOpen(false);
    const handleWarehouseSelected = (warehouse: WarehouseModel) => {
        if (onChange) {
            onChange(warehouse);
            setIsFindLocationOpen(false);
        }
    };
    return (
        <GridContainer {...styles.container} data-test-selector="checkoutShipping_pickUpAddress">
            <GridItem {...styles.headingGridItem}>
                <Typography {...styles.headingText} as="h3">
                    {translate("Pick Up Address")}
                </Typography>
                <Link
                    {...styles.findLocationLink}
                    type="button"
                    onClick={handleOpenFindLocation}
                    data-test-selector="checkoutShipping_pickUpAddress_findLocation"
                >
                    {translate("Find Location")}
                </Link>
            </GridItem>
            {address && (
                <GridItem {...styles.selectedLocationGridItem}>
                    <Typography
                        {...styles.selectedLocationName}
                        data-test-selector="checkoutShipping_pickUpAddress_warehouseName"
                    >
                        {address.description || address.name}
                    </Typography>
                    <AddressInfoDisplay {...address} extendedStyles={styles.selectedLocationAddressDisplay} />
                </GridItem>
            )}
            <FindLocationModal
                modalIsOpen={isFindLocationOpen}
                onWarehouseSelected={handleWarehouseSelected}
                onModalClose={handleFindLocationModalClose}
                forCart
                extendedStyles={styles.findLocationModal}
            />
        </GridContainer>
    );
};

export default PickUpAddress;
